import { useState, useRef, useEffect, useMemo } from 'react'
import {Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, X} from 'lucide-react'

interface DateTimePickerProps {
  value?: string // ISO string
  onChange: (iso: string) => void
  placeholder?: string
  minDate?: Date
  allowClear?: boolean
}

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
]
const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

function pad(n: number) {
  return String(n).padStart(2, '0')
}

function formatDisplay(d?: Date): string {
  if (!d) return ''
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()].toLowerCase().slice(0, 3)}. ${d.getFullYear()} · ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export default function DateTimePicker({ value, onChange, placeholder = 'Choisir une date…', minDate, allowClear = true }: DateTimePickerProps) {
  const currentDate = useMemo(() => (value ? new Date(value) : undefined), [value])
  const [open, setOpen] = useState(false)
  const [viewMonth, setViewMonth] = useState<Date>(() => currentDate || new Date())
  const [hour, setHour] = useState<number>(currentDate?.getHours() ?? 9)
  const [minute, setMinute] = useState<number>(currentDate?.getMinutes() ?? 0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (currentDate) {
      setViewMonth(currentDate)
      setHour(currentDate.getHours())
      setMinute(currentDate.getMinutes())
    }
  }, [value])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const daysInGrid = useMemo(() => {
    const year = viewMonth.getFullYear()
    const month = viewMonth.getMonth()
    const first = new Date(year, month, 1)
    const startOffset = (first.getDay() + 6) % 7 // Monday = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const cells: Array<{ date: Date; inMonth: boolean }> = []
    // Previous month tail
    for (let i = startOffset; i > 0; i--) {
      const d = new Date(year, month, 1 - i)
      cells.push({ date: d, inMonth: false })
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(year, month, d), inMonth: true })
    }
    // Next month to fill 6 rows (42 cells)
    while (cells.length < 42) {
      const last = cells[cells.length - 1].date
      const d = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1)
      cells.push({ date: d, inMonth: d.getMonth() === month })
    }
    return cells
  }, [viewMonth])

  const today = new Date()

  const commit = (d: Date, h: number, m: number) => {
    const final = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0, 0)
    onChange(final.toISOString())
  }

  const handleDayClick = (d: Date) => {
    if (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) return
    commit(d, hour, minute)
  }

  const handleTimeChange = (h: number, m: number) => {
    setHour(h)
    setMinute(m)
    if (currentDate) commit(currentDate, h, m)
  }

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
  }

  const goToday = () => {
    const now = new Date()
    setViewMonth(now)
    commit(now, hour, minute)
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 bg-[#262626] border border-[#c9a96e]/20 text-[#f5f1ea] px-3.5 py-2.5 text-sm font-light hover:border-[#c9a96e]/50 focus:border-[#c9a96e] outline-none transition-colors"
      >
        <span className="flex items-center gap-2.5 min-w-0 truncate">
          <CalendarIcon className="w-3.5 h-3.5 text-[#c9a96e] flex-shrink-0" />
          <span className={`truncate ${currentDate ? 'text-[#f5f1ea]' : 'text-[#a8a39e]'}`}>
            {currentDate ? formatDisplay(currentDate) : placeholder}
          </span>
        </span>
        {allowClear && currentDate && (
          <span
            role="button"
            tabIndex={0}
            onClick={clear}
            className="p-0.5 text-[#a8a39e] hover:text-red-400 transition-colors cursor-pointer"
            title="Effacer"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-[320px] bg-[#262626] border border-[#c9a96e]/40 shadow-2xl shadow-black/60 p-4 animate-[fadeIn_0.15s_ease-out]">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              className="p-1.5 text-[#8a8580] hover:text-[#c9a96e] border border-[#c9a96e]/15 hover:border-[#c9a96e]/50 transition-all"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-2">
              <select
                value={viewMonth.getMonth()}
                onChange={(e) => setViewMonth(new Date(viewMonth.getFullYear(), parseInt(e.target.value), 1))}
                className="bg-transparent text-[#f5f1ea] font-serif text-sm border-none outline-none cursor-pointer hover:text-[#c9a96e]"
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i} className="bg-[#262626]">{m}</option>
                ))}
              </select>
              <select
                value={viewMonth.getFullYear()}
                onChange={(e) => setViewMonth(new Date(parseInt(e.target.value), viewMonth.getMonth(), 1))}
                className="bg-transparent text-[#c9a96e] font-serif text-sm border-none outline-none cursor-pointer"
              >
                {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 1 + i).map((y) => (
                  <option key={y} value={y} className="bg-[#262626]">{y}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              className="p-1.5 text-[#8a8580] hover:text-[#c9a96e] border border-[#c9a96e]/15 hover:border-[#c9a96e]/50 transition-all"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Weekdays header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="text-center text-[10px] tracking-widest uppercase text-[#a8a39e]/60 py-1">
                {w}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {daysInGrid.map((cell, i) => {
              const isSelected = currentDate && isSameDay(cell.date, currentDate)
              const isToday = isSameDay(cell.date, today)
              const isDisabled = minDate ? cell.date < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate()) : false
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleDayClick(cell.date)}
                  disabled={isDisabled}
                  className={`aspect-square flex items-center justify-center text-xs font-light transition-all ${
                    isSelected
                      ? 'bg-[#c9a96e] text-[#262626] font-semibold'
                      : isToday
                      ? 'border border-[#c9a96e]/60 text-[#c9a96e]'
                      : cell.inMonth
                      ? 'text-[#f5f1ea]/80 hover:bg-[#c9a96e]/15 hover:text-[#c9a96e]'
                      : 'text-[#a8a39e]/30 hover:text-[#a8a39e]/70'
                  } ${isDisabled ? 'opacity-30 cursor-not-allowed hover:bg-transparent' : ''}`}
                >
                  {cell.date.getDate()}
                </button>
              )
            })}
          </div>

          {/* Time picker */}
          <div className="mt-4 pt-4 border-t border-[#c9a96e]/15">
            <div className="flex items-center gap-3">
              <Clock className="w-3.5 h-3.5 text-[#c9a96e]" />
              <span className="text-[10px] tracking-widest uppercase text-[#a8a39e]">Heure</span>
              <div className="flex-1 flex items-center gap-2">
                <select
                  value={hour}
                  onChange={(e) => handleTimeChange(parseInt(e.target.value), minute)}
                  className="flex-1 bg-[#0a0a0a] border border-[#c9a96e]/20 text-[#f5f1ea] px-2 py-1.5 text-sm font-light outline-none focus:border-[#c9a96e] cursor-pointer"
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                    <option key={h} value={h} className="bg-[#0a0a0a]">{pad(h)}</option>
                  ))}
                </select>
                <span className="text-[#c9a96e]">:</span>
                <select
                  value={minute}
                  onChange={(e) => handleTimeChange(hour, parseInt(e.target.value))}
                  className="flex-1 bg-[#0a0a0a] border border-[#c9a96e]/20 text-[#f5f1ea] px-2 py-1.5 text-sm font-light outline-none focus:border-[#c9a96e] cursor-pointer"
                >
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                    <option key={m} value={m} className="bg-[#0a0a0a]">{pad(m)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-[#c9a96e]/15 flex items-center justify-between">
            <button
              type="button"
              onClick={goToday}
              className="text-[10px] tracking-widest uppercase text-[#a8a39e] hover:text-[#c9a96e] transition-colors"
            >
              Aujourd'hui
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-3 py-1.5 bg-[#c9a96e] text-[#262626] hover:bg-[#d4b47a] text-[10px] tracking-widest uppercase font-semibold transition-all"
            >
              Valider
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
